import React from 'react';
import styled from 'styled-components';
import { Badge } from '../components/Decorative';
import { Text } from '../components/Typography';
import { Flex } from '../components/Layout';
import { Icon } from '../../components/common/Icon';
import { iconMappings } from '../../utils/iconMappings';
import { spacing } from '../tokens/spacing';

export type StatusType = 'success' | 'warning' | 'error' | 'info' | 'pending' | 'completed';

interface StatusIndicatorProps {
  status: StatusType;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

const statusConfig: Record<StatusType, { 
  color: 'success' | 'warning' | 'error' | 'info' | 'muted' | 'primary'; 
  icon: any; 
  label: string 
}> = {
  success: { color: 'success', icon: iconMappings.success, label: 'Success' },
  warning: { color: 'warning', icon: iconMappings.warning, label: 'Warning' },
  error: { color: 'error', icon: iconMappings.error, label: 'Error' },
  info: { color: 'info', icon: iconMappings.pending, label: 'Info' },
  pending: { color: 'muted', icon: iconMappings.pending, label: 'Pending' },
  completed: { color: 'success', icon: iconMappings.success, label: 'Completed' },
};

const IndicatorContainer = styled(Flex)`
  align-items: center;
`;

const IconSizeMap = {
  sm: 'sm' as const,
  md: 'md' as const,
  lg: 'lg' as const,
};

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  label,
  size = 'md',
  showIcon = true,
  className,
}) => {
  const config = statusConfig[status];
  const displayLabel = label || config.label;

  return (
    <IndicatorContainer direction="row" gap="sm" className={className}>
      {showIcon && config.icon && (
        <Icon 
          icon={config.icon} 
          size={IconSizeMap[size]} 
          color={config.color}
        />
      )}
      <Badge size={size} color={config.color} shape="rounded">
        {displayLabel}
      </Badge>
    </IndicatorContainer>
  );
};

export default StatusIndicator;
